/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAuthenticationRuleList.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    Custom hook for managing Authentication Rules list.
    Integrates with useDataGrid for pagination, sorting, and search.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchAuthenticationRules } from '../api';
import { AuthenticationRule } from '../types';

interface UseAuthenticationRuleListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
  initialColumns?: DataGridColumn<AuthenticationRule>[];
}

/**
 * Custom hook for Authentication Rules list
 * Implements Domain Contextual Filtering pattern
 */
export function useAuthenticationRuleList(options: UseAuthenticationRuleListOptions = {}) {
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchAuthenticationRules({
          ...params,
          domainId: effectiveDomainId,
        });
        return {
          data: result.data || [],
          pagination: result.pagination || {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 1,
          },
        };
      } catch (error) {
        return {
          data: [] as AuthenticationRule[],
          pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
        };
      }
    },
    [effectiveDomainId],
  );

  const gridResult = useDataGrid({
    fetchData,
    initialColumns: options.initialColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'authenticationCodes',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    ...gridResult,
    effectiveDomainId,
  };
}

